import {useCallback, useEffect, useState} from "react";
import '@inovua/reactdatagrid-community/index.css'
import {useFormikContext} from "formik";
import {euroFormat} from "../../../utils/usefullFunctions";
import {Col, Form, Row} from "react-bootstrap";
import notyfApi from "../../../utils/notyfApi";
import Select from "react-select";
import portalApi from "../../../api/portal";
import CustomDataGrid from "../../table/CustomDataGrid";

// Products tab where products can be added and removed
const Products = () => {
    const {values, setFieldValue} = useFormikContext();
    const {service_form_project_data_product} = values
    const [service_form_project_options, setOptions] = useState([]) // Product options
    const [gridRef, setGridRef] = useState(null)

    const cellDOMProps = (cellProps) => { // allows the use of just one click to edit a cell
        return {
            onClick: () => {
                gridRef.current.startEdit({columnId: cellProps.id, rowIndex: cellProps.rowIndex})
            }
        }
    }
    const columns = [
        {name: 'amount', header: '#', defaultFlex: 1, minWidth: 50, type: 'number', cellDOMProps},
        {
            name: 'article', header: 'Artikelnummer', minWidth: 200, defaultFlex: 1, cellDOMProps,
            onRender: (cellProps, {data}) => {
                if (!data.cost_st) cellProps.style.color = "#000"
            }
        },
        {name: 'article_description', header: 'Omschrijving', minWidth: 300, defaultFlex: 1, cellDOMProps},
        {
            name: 'cost_st', header: 'Cost/st', minWidth: 100, defaultFlex: 1, cellDOMProps,
            render: ({value}) => {
                if (value) return euroFormat(value)
            }
        },
        {
            name: 'cost_tot', header: 'Cost', minWidth: 100, defaultFlex: 1, editable: false,
            render: ({value}) => {
                if (value) return euroFormat(value)
            },
            onRender: (cellProps) => {
                cellProps.style.background = '#f8f8f8'
            }
        },
        {
            name: 'list_st', header: 'Bruto/st', minWidth: 100, defaultFlex: 1, cellDOMProps,
            render: ({value}) => {
                if (value) return euroFormat(value)
            }
        },
        {
            name: 'list_total', header: 'Bruto', minWidth: 100, defaultFlex: 1, editable: false,
            render: ({value}) => {
                if (value) return euroFormat(value)
            },
            onRender: (cellProps) => {
                cellProps.style.background = '#f8f8f8'
            }
        },
        {
            name: 'row_description', header: 'Toelichting', minWidth: 200, defaultFlex: 1, cellDOMProps,
        }
    ]

    const [searchTerm, setSearchTerm] = useState("")

    const [dataSource, setDataSource] = useState(service_form_project_data_product);

    // Update the context values
    useEffect(() => {
        const totals = dataSource.reduce((prev, curr) => {
            prev.cost = prev.cost + parseFloat(curr.cost_tot || 0)
            prev.list = prev.list + parseFloat(curr.list_total || 0)
            return prev
        }, {
            cost: 0,
            list: 0,
        })
        setFieldValue("service_form_project_products_cost", totals)
        setFieldValue("service_form_project_data_product", dataSource)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSource])

    // When the search term is changed get options
    useEffect(() => {
        getOptions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm])

    const getOptions = async () => {
        if (!searchTerm) return setOptions([]) // Check if there is a search term
        const res = await portalApi.getProducts(searchTerm)
        if (!res.ok) return notyfApi.error("Error met opties ophalen")

        // Loop through results and set the options
        let temp = []
        for (let p of res.data.products) {
            temp.push({
                value: p,
                label: p.product_name + " - " + p.product_description
            })

        }
        setOptions(temp)
    }


    // Options for custom context menu in the table
    const renderRowContextMenu = (menuProps) => {
        menuProps.autoDismiss = true

        menuProps.items = [
            {
                label: "Rij toevoegen",
                onClick: () => addRow()
            },
            {
                label: "Rij verwijderen",
                onClick: () => setDataSource(dataSource.slice(0, -1))
            },
        ]
    }

    // This handles the editing of the table and adjust the other columns as well
    const onEditComplete = useCallback(({value, columnId, rowId}) => {
        const data = [...dataSource];
        if (columnId === "amount") {
            if (!data[rowId]["cost_st"] || !value) return null
            value = parseInt(value || 0)
            data[rowId][columnId] = value;
            data[rowId]["cost_tot"] = value * data[rowId]["cost_st"];
            data[rowId]["list_total"] = value * data[rowId]["list_st"];
        }
        else if ([ "cost_st", "list_st"].includes(columnId)) {
            value = parseFloat(value || 0).toFixed(2)
            data[rowId][columnId] = value;
            if (!data[rowId]["amount"]) return null
            data[rowId]["cost_tot"] = data[rowId]["amount"] * data[rowId]["cost_st"];
            data[rowId]["list_total"] = data[rowId]["amount"] * data[rowId]["list_st"];
        } else {
            data[rowId][columnId] = value;
        }
        setDataSource(data);
    }, [dataSource])

    // Add a row to the table, always at the bottom
    const addRow = useCallback(() => {

        const data = [...dataSource, {id: dataSource.length}];
        setDataSource(data);
    }, [dataSource])

    // Special key handler for the search select options
    const keyHandler = e => {
        //searchquery always 1 letter behind, but fixed by adding * on serverside
        const input = String.fromCharCode(e.keyCode);

        if (/[a-zA-Z\d]/.test(input)) setSearchTerm(e.target.value + e.key)
        console.log(searchTerm)
        if (e.keyCode === 8) setSearchTerm(searchTerm.slice(0, -1)) // When backspace pressed remove last key
    }

    // On select this handler adds the product to the row
    const selectHandler = e => {
        const data = [...dataSource,
            {
                id: dataSource.length,
                amount: 1,
                article: e.product_name,
                article_description: e.product_description,
                cost_st: parseFloat(e.product_cost),
                cost_tot: parseFloat(e.product_cost),
                list_st: parseFloat(e.product_list),
                list_total: parseFloat(e.product_list),
                row_description: e.product_comment,
            }
        ];
        setDataSource(data);
    }

    return <div>
        <CustomDataGrid
            idProperty="id"
            onEditComplete={onEditComplete}
            editable={true}
            columns={columns}
            dataSource={dataSource}
            renderRowContextMenu={renderRowContextMenu}
            onReady={setGridRef}
        />

        <Row>
            <Col lg={6}>
                <Form.Label>Product:</Form.Label>
                <Select options={service_form_project_options}
                        onKeyDown={keyHandler}
                        onChange={e => selectHandler(e.value)}
                        className={"mb-3 p-0"}
                        noOptionsMessage={() => 'Er zijn geen producten gevonden'}
                        menuPlacement={"top"}

                />
            </Col>
        </Row>
    </div>
}


export default Products